/* Courted · Perfect days, the pure part (tests/courted.test.js). A perfect day is the household's: a day, an evening
   or a weekend (Friday evening to Sunday night), near home or in one of the household's towns, with whatever you'd do
   by day and part of the day. This says what to call it, how it reads, when you're next both free for it (Home's
   free time), and the times it goes on the week at. No DOM, no storage. */
import { USUAL_MINUTES, ampm, addDays } from './helpers.js';

export const PARTS = [
  { id: 'morning', label: 'Morning' },
  { id: 'afternoon', label: 'Afternoon' },
  { id: 'evening', label: 'Evening' },
  { id: 'night', label: 'Night' },
];
const ORDER = { morning: 0, afternoon: 1, evening: 2, night: 3 };
export const PART_START = { morning: 10 * 60, afternoon: 13 * 60, evening: 18 * 60 + 30, night: 22 * 60 };
const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const NUM = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
const dateOf = (iso) => new Date(iso + 'T12:00:00');
const q15 = (m) => Math.ceil(m / 15) * 15;   // "from 7:15pm", never "from 7:12pm"
const at = (m) => ampm(q15(m));
const isUS = (cc) => String(cc || '').toUpperCase() === 'US';

/** A weekend's days are Friday, Saturday and Sunday; a day has one. */
export const dayNames = (n) => (n === 3 ? ['Friday', 'Saturday', 'Sunday'] : ['']);
/** The parts of a day: a weekend starts on Friday evening. */
export const partsOn = (n, day) => (n === 3 && day === 0 ? PARTS.slice(2) : PARTS);
export const partLabel = (id) => (PARTS.find((p) => p.id === id) || PARTS[2]).label;

/** In order: by day, part, then where each was put. */
export function sorted(items) {
  return [...(items || [])].sort((a, b) => (a.day - b.day) || (ORDER[a.part] - ORDER[b.part]) || ((a.pos || 0) - (b.pos || 0)) || (String(a.created_at) < String(b.created_at) ? -1 : 1));
}
export const inPart = (items, day, part) => sorted(items).filter((x) => x.day === day && x.part === part);
export const nextPos = (items, day, part) => inPart(items, day, part).reduce((m, x) => Math.max(m, (x.pos || 0) + 1), 0);

/** One day with nothing before the evening is an evening. */
export const isEvening = (p, items) => p.days !== 3 && (items || []).length > 0 && items.every((x) => x.part === 'evening' || x.part === 'night');
export const lengthWords = (p, items) => (p.days === 3 ? 'Friday to Sunday' : isEvening(p, items) ? 'An evening' : 'A day');

/** What to call a new one: "A weekend in Paris", "A day at home". */
export function defaultTitle(days, town) {
  if (days === 3) return 'A weekend ' + (town ? 'in ' + town.name : 'at home');
  return town ? 'A day in ' + town.name : 'A day at home';
}

/* In a line of things, the ones that start with an everyday word read on in lower case ("Osteria Lucia, a comedy
   show, then Bar Ardoise"); a name keeps its capital. */
const EVERYDAY = /^(a|an|the|some|our|dinner|drinks|lunch|brunch|breakfast|coffee|cocktails|bowling|walk|picnic|pancakes|lie-in|swim|nap|movie|film|cook|bake|games?|sleep|out|something|tea|cake|dessert|wine|ice cream)\b/i;
const soft = (s, i) => (i > 0 && EVERYDAY.test(s) && !/^[A-Z]{2}/.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s);
/** "Osteria Lucia, a comedy show, then Bar Ardoise"; "Café Margaux, Galerie Lumen, a walk along the Seine and 6 more". */
export function summary(items, nameOf, max = 3) {
  const w = sorted(items).map((x) => String((nameOf && nameOf(x)) || x.label || '').trim()).filter(Boolean).map(soft);
  if (!w.length) return '';
  if (w.length <= max) return w.length === 1 ? w[0] : w.slice(0, -1).join(', ') + ', then ' + w[w.length - 1];
  return w.slice(0, max).join(', ') + ' and ' + (w.length - max) + ' more';
}
export const countWords = (n) => (n <= 10 ? NUM[n] : String(n)) + (n === 1 ? ' thing' : ' things');

/** Who's put things in it (or made it): ids, you first. */
export function whoAdded(p, items, me) {
  const ids = [...new Set([...(items || []).map((x) => x.added_by), p.created_by].filter(Boolean))];
  return ids.sort((a, b) => (a === me ? -1 : b === me ? 1 : 0));
}

/** "Your Toronto evening?" for one beginning with A; "A weekend in Paris?" otherwise. */
export function askTitle(p) {
  const t = String(p.title || '').trim();
  return p.days !== 3 && /^an? /i.test(t) ? 'Your ' + t.replace(/^an? /i, '') + '?' : t + '?';
}

/* ---- when it fits ---- */
const hm = (t) => (t ? Number(String(t).slice(0, 2)) * 60 + Number(String(t).slice(3, 5)) : null);
/** The plans on each date, as Courted reads them (plans for either of you, still planned). → { date: [{ start, all }] } */
export function busyFrom(plans) {
  const out = {};
  (plans || []).forEach((p) => { (out[p.on_date] = out[p.on_date] || []).push({ start: hm(p.start_time), all: !!p.all_day }); });
  return out;
}
/* When you're both free on a date: { from } for an evening (home by 8:30pm), { all } for the whole day, else null.
   The week ahead is Home's `days` (what's planned taken off); further out, its `evenings` (who's out, not what's
   planned: `busy` covers that). */
function freeOn(date, free, today) {
  if (!free) return null;
  const ahead = date >= today && date <= addDays(today, 6) && Array.isArray(free.days);
  if (ahead) {
    const d = free.days.find((x) => x.date === date);
    if (!d) return null;
    return d.all ? { all: true, from: d.from } : d.from <= 20 * 60 + 30 ? { from: d.from } : null;
  }
  const e = free.evenings && free.evenings[date];
  if (!e || !e.both) return null;
  return e.all ? { all: true } : e.from != null ? { from: e.from } : null;
}
const eveningBusy = (list) => (list || []).some((b) => b.all || b.start == null || b.start >= 16 * 60);
const dayBusy = (list) => (list || []).length > 0;

/** The next times you're both free for it, from today, up to `weeks` ahead: an evening when you're both home by
    8:30pm (or free all day), a day when you're both free all day, a weekend when Friday evening, Saturday and Sunday
    are. Not on a day with something planned then (`busy`, from busyFrom). → [{ date, end, from, all }] */
export function fitDates(p, items, { free, busy, today, n = 3, weeks = 8 } = {}) {
  const out = [];
  const b = busy || {};
  const ev = isEvening(p, items) || !(items || []).length;
  for (let i = 0; i < weeks * 7 && out.length < n; i++) {
    const date = addDays(today, i), dow = dateOf(date).getDay();
    if (p.days === 3) {
      if (dow !== 5) continue;
      const fri = freeOn(date, free, today), sat = freeOn(addDays(date, 1), free, today), sun = freeOn(addDays(date, 2), free, today);
      if (fri && sat && sat.all && sun && sun.all && !eveningBusy(b[date]) && !dayBusy(b[addDays(date, 1)]) && !dayBusy(b[addDays(date, 2)])) {
        out.push({ date, end: addDays(date, 2), from: fri.all ? 17 * 60 : fri.from, all: false });
      }
      continue;
    }
    const f = freeOn(date, free, today);
    if (!f) continue;
    if (ev) { if (!eveningBusy(b[date])) out.push({ date, end: date, from: f.all ? null : f.from, all: !!f.all }); }
    else if (f.all && !dayBusy(b[date])) out.push({ date, end: date, from: null, all: true });
  }
  return out;
}

/** "Saturday 7 November" ("Saturday, November 7" in the US); a weekend "20–22 November" ("November 20–22"). */
export function dateLabel(fit, cc) {
  const a = dateOf(fit.date);
  if (fit.end && fit.end !== fit.date) {
    const z = dateOf(fit.end);
    const same = a.getMonth() === z.getMonth();
    if (isUS(cc)) return MONTHS[a.getMonth()] + ' ' + a.getDate() + '–' + (same ? '' : MONTHS[z.getMonth()] + ' ') + z.getDate();
    return a.getDate() + (same ? '' : ' ' + MONTHS[a.getMonth()]) + '–' + z.getDate() + ' ' + MONTHS[z.getMonth()];
  }
  return isUS(cc) ? DOW[a.getDay()] + ', ' + MONTHS[a.getMonth()] + ' ' + a.getDate() : DOW[a.getDay()] + ' ' + a.getDate() + ' ' + MONTHS[a.getMonth()];
}
/** "today", "tomorrow", "Saturday", or the date when it's a week or more away. */
export function dayWord(date, today, cc) {
  if (date === today) return 'today';
  if (date === addDays(today, 1)) return 'tomorrow';
  if (date <= addDays(today, 6)) return DOW[dateOf(date).getDay()];
  return dateLabel({ date }, cc);
}
/** Under a date: "You're both free from 6pm", "You're both free all day", "From 6pm on Friday". */
export function fitLine(p, fit, solo) {
  const you = solo ? 'You’re free' : 'You’re both free';
  if (p.days === 3) return 'From ' + at(fit.from) + ' on Friday';
  return fit.all ? you + ' all day' : you + ' from ' + at(fit.from);
}
/** On the list: "You're both free Saturday from 6pm", "Your next free weekend is 20–22 Nov". */
export function nextLine(p, items, fits, { today, cc, solo, weekKnown, planned } = {}) {
  if (planned && planned >= today) { const w = dayWord(planned, today, cc); return 'On your week ' + (w === 'today' || w === 'tomorrow' ? w : 'for ' + w); }
  if (!(items || []).length) return 'Nothing in it yet';
  if (!weekKnown) return '';
  const f = fits && fits[0];
  if (!f) return p.days === 3 ? 'No free weekend in the next eight weeks' : 'Not in the next eight weeks';
  if (p.days === 3) return 'Your next free weekend is ' + dateLabel(f, cc).replace(/(January|February|March|April|May|June|July|August|September|October|November|December)/, (m) => m.slice(0, 3));
  const w = dayWord(f.date, today, cc);
  const you = solo ? 'You’re free ' : 'You’re both free ';
  return you + (w === 'today' ? (f.all ? 'all day today' : 'tonight from ' + at(f.from)) : w + (f.all ? ' all day' : ' from ' + at(f.from)));
}

/* ---- the times it goes on the week at ---- */
const round15 = q15;
/** How long each thing takes: a place by its kind, a recipe as a meal at home, a show an episode, a film a film. */
export function itemMinutes(x) {
  if (x.kind === 'place') return USUAL_MINUTES[x.place_kind] || 120;
  if (x.kind === 'recipe' || x.kind === 'cook') return 75;
  if (x.kind === 'title') return String(x.ref || '').startsWith('tv:') ? 60 : 120;
  return 90;
}
/** Each thing's start: its part's usual start (morning 10am, afternoon 1pm, evening 6:30pm, night 10pm), or after
    the thing before it that day; the evening from when you're both free (`from`). → { id: { day, start, minutes } } */
export function planTimes(p, items, { from } = {}) {
  const out = {};
  const list = sorted(items);
  const days = p.days === 3 ? [0, 1, 2] : [0];
  for (const day of days) {
    let t = 0;
    for (const part of PARTS.map((x) => x.id)) {
      let first = PART_START[part];
      if (part === 'evening' && day === 0 && from != null) first = Math.max(first, round15(from));
      for (const x of list.filter((y) => y.day === day && y.part === part)) {
        const start = Math.min(23 * 60 + 45, Math.max(first, t));
        const minutes = itemMinutes(x);
        out[x.id] = { day, start, minutes };
        t = round15(start + minutes);
        first = t;
      }
    }
  }
  return out;
}

/* ---- Home's card ---- */
/** The perfect day to suggest: one you're both free for in the next fortnight (a weekend, the next four weeks), not
    already on the week, not set aside this week (`no`: { id: until }). The soonest first, then the fullest.
    → { p, fit } | null */
export function suggestion(list, { free, busy, today, no } = {}) {
  let best = null;
  for (const p of list || []) {
    if (!(p.items || []).length) continue;
    if (p.planned_on && p.planned_on >= today) continue;
    if (no && no[p.id] && no[p.id] >= today) continue;
    const fit = fitDates(p, p.items, { free, busy, today, n: 1, weeks: p.days === 3 ? 4 : 2 })[0];
    if (!fit) continue;
    if (!best || fit.date < best.fit.date || (fit.date === best.fit.date && p.items.length > best.p.items.length)) best = { p, fit };
  }
  return best;
}
/** Its words: "You're both free Saturday from 6pm. Osteria Lucia, a comedy show, then Bar Ardoise."; a weekend "You're
    both free 20–22 November. Nine things saved for it." */
export function suggestionLine(s, { today, cc, solo, nameOf } = {}) {
  const { p, fit } = s;
  const you = solo ? 'You’re free ' : 'You’re both free ';
  if (p.days === 3) return you + dateLabel(fit, cc) + '. ' + countWords(p.items.length) + ' saved for it.';
  const w = dayWord(fit.date, today, cc);
  const when = w === 'today' ? (fit.all ? 'all day today' : 'tonight from ' + at(fit.from)) : w + (fit.all ? ' all day' : ' from ' + at(fit.from));
  return you + when + '. ' + summary(p.items, nameOf) + '.';
}
